<template>
  <Teleport to="body">
    <transition name="v2-hub">
      <div
        v-if="open"
        class="v2-hub"
        @click.self="close"
      >
        <div
          ref="panel"
          class="v2-hub__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="v2-hub-title"
          aria-describedby="v2-hub-intro"
          @keydown.tab="trapFocus"
        >
          <!-- Floating close — same flat pink circle every v2 modal uses. -->
          <button
            ref="closeBtn"
            type="button"
            class="v2-hub__close"
            aria-label="Close"
            @click="close"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.25" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18"/>
            </svg>
          </button>

          <div class="v2-hub__scroll">
            <header class="v2-hub__header">
              <span class="v2-hub__eyebrow">
                <span class="v2-hub__eyebrow-dot" aria-hidden="true"></span>
                Support our campaign
              </span>
              <h2 id="v2-hub-title" class="v2-hub__title">{{ displayName }}</h2>
              <a
                v-if="parentCampaign"
                class="v2-hub__parent"
                :href="`/campaigns/${parentCampaign.id}`"
              >
                Supporting {{ parentCampaign.name }} <span aria-hidden="true">→</span>
              </a>
              <p class="v2-hub__headline">{{ headlineText }}</p>
              <div id="v2-hub-intro" class="v2-hub__intro" v-html="introHtml"></div>
            </header>

            <div class="v2-hub__cards">
              <!-- ── Fundraise ─────────────────────────────────────── -->
              <article class="v2-hub__card v2-hub__card--fundraise" aria-labelledby="v2-hub-fundraise-title">
                <div class="v2-hub__card-head">
                  <span class="v2-hub__card-icon v2-hub__card-icon--blue" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
                      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
                      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
                      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
                    </svg>
                  </span>
                  <div>
                    <h3 id="v2-hub-fundraise-title" class="v2-hub__card-title">Become a Team Fundraiser</h3>
                    <p class="v2-hub__card-sub">Rally your circle behind the team — and the patients Side-Out fights for.</p>
                  </div>
                </div>

                <ul class="v2-hub__benefits">
                  <li v-for="benefit in benefits" :key="benefit.title">
                    <span class="v2-hub__check" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span>
                      <strong>{{ benefit.title }}</strong>
                      {{ benefit.body }}
                    </span>
                  </li>
                </ul>

                <button
                  type="button"
                  class="v2-hub__cta v2-hub__cta--blue"
                  @click="startFundraiser"
                >
                  {{ ctaLabel }}
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </button>
                <V2RegistrationLink v-if="campaign?.id" :campaign-id="campaign.id" />
              </article>

              <!-- ── Share ─────────────────────────────────────────── -->
              <article class="v2-hub__card v2-hub__card--share" aria-labelledby="v2-hub-share-title">
                <div class="v2-hub__card-head">
                  <span class="v2-hub__card-icon v2-hub__card-icon--pink" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="m3 11 18-5v12L3 14v-3z"/>
                      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
                    </svg>
                  </span>
                  <div>
                    <h3 id="v2-hub-share-title" class="v2-hub__card-title">Spread the Word, Spread the Mission</h3>
                    <p class="v2-hub__card-sub">Every share puts the team — and Side-Out's fight — in front of someone new.</p>
                  </div>
                </div>

                <div class="v2-hub__qr-panel">
                  <div v-if="qrSvg" class="v2-hub__qr" role="img" aria-label="QR code for this campaign" v-html="qrSvg"></div>
                  <div v-else class="v2-hub__qr v2-hub__qr--placeholder" aria-hidden="true"></div>
                  <div class="v2-hub__qr-copy">
                    <span class="v2-hub__qr-label">Sharing in person?</span>
                    <h4>Just show this code</h4>
                    <p>Point any phone camera at it — the campaign page opens instantly.</p>
                  </div>
                </div>

                <div class="v2-hub__copy-row">
                  <input
                    ref="campaignLinkInput"
                    type="text"
                    class="v2-hub__url"
                    :value="campaignShareUrl"
                    readonly
                    aria-label="Campaign link"
                    @focus="$event.target.select()"
                  />
                  <button
                    type="button"
                    class="v2-hub__copy-btn"
                    :class="{ 'is-copied': campaignLinkCopied }"
                    @click="copyCampaignLink"
                  >
                    {{ campaignLinkCopied ? 'Copied!' : 'Copy link' }}
                  </button>
                </div>

                <div class="v2-hub__social-grid">
                  <button
                    v-for="opt in shareOptions"
                    :key="opt.key"
                    type="button"
                    :class="['v2-hub__social', 'v2-hub__social--' + opt.key]"
                    @click="openShare(opt)"
                  >
                    <span class="v2-hub__social-icon" aria-hidden="true" v-html="opt.icon"></span>
                    <span>{{ opt.label }}</span>
                  </button>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import V2RegistrationLink from './V2RegistrationLink.vue'
import { acquirePageScrollLock } from '../../../../composables/usePageScrollLock'
import {
  buildShareOptions,
  cleanCampaignShareUrl,
  copyTextToClipboard,
  isDesktop,
  openShareOption,
} from '../../../../composables/useCampaignShare'

// Published Side-Out copy — used whenever the foundation template leaves a
// field blank. Mirrors the in-page V2SupportOnboarding section so the modal
// and the section never say different things.
const DEFAULT_HEADLINE = 'Help Us Reach Our Goal'
const DEFAULT_INTRO = "Create your own personal fundraising page to support our Dig Pink® campaign. Every dollar you raise feeds directly into our team's goal, and together, we'll make a bigger impact."
const DEFAULT_CTA = 'Support Our Campaign'

const BENEFITS = [
  { title: 'Set up in under 2 minutes', body: 'A guided flow gets you a personal fundraising page.' },
  { title: 'Tell people why this matters to you', body: 'Add your photo, your story, and your goal.' },
  { title: 'Every dollar fuels the mission', body: 'Funds roll up to our team total and into Side-Out research.' },
]

/**
 * V2SupportHubModal — the "Support our campaign" takeover.
 *
 * One modal that presents both ways to help without donating: start a
 * supporting fundraiser (hands off to V2SupportModal's signup wizard) or
 * share the campaign (QR, copy link, social intents). It replaces the
 * standalone /campaigns/{id}/support Blade page; that URL now redirects to
 * the campaign with `?support=1`, which the page turns into `open=true`.
 *
 * Prop-driven like V2SupportModal so DigPinkCampaignPageV2 can track it for
 * the mobile sticky bar and own the URL-flag lifecycle.
 */
export default {
  name: 'V2SupportHubModal',
  components: { V2RegistrationLink },
  props: {
    open: { type: Boolean, required: true },
    campaign: { type: Object, default: null },
    /** Foundation template `support_onboarding` block (headline/intro/cta_label). */
    supportOnboarding: { type: Object, default: () => ({}) },
    /** Absolute shareable URL that reopens this modal (route campaign.support). */
    supportPageUrl: { type: String, default: '' },
    /** Element to refocus on close when the opener is no longer visible. */
    returnFocus: { default: null },
  },
  emits: ['close', 'start-fundraiser'],
  data() {
    return {
      benefits: BENEFITS,
      campaignShareUrl: '',
      qrSvg: '',
      campaignLinkCopied: false,
      previouslyFocused: null,
    }
  },
  computed: {
    displayName() {
      return this.campaign?.business_name || this.campaign?.name || 'this campaign'
    },
    parentCampaign() {
      const parent = this.campaign?.parent
      return parent && parent.id ? parent : null
    },
    headlineText() {
      return this.cleanString(this.supportOnboarding?.headline) || DEFAULT_HEADLINE
    },
    introHtml() {
      // Admin HTML is sanitized at save time (FoundationTemplateContent).
      return this.cleanString(this.supportOnboarding?.intro) || DEFAULT_INTRO
    },
    ctaLabel() {
      return this.cleanString(this.supportOnboarding?.cta_label) || DEFAULT_CTA
    },
    shareOptions() {
      // The Copy link button beside the URL already owns copying.
      return buildShareOptions({
        url: this.campaignShareUrl,
        campaignName: this.campaign?.name,
        desktop: isDesktop(),
      }).filter(opt => opt.key !== 'copy')
    },
  },
  watch: {
    open(nowOpen) {
      if (nowOpen) this.onOpen()
      else this.onClose()
    },
  },
  beforeUnmount() {
    this.onClose()
  },
  methods: {
    cleanString(value) {
      return typeof value === 'string' ? value.trim() : ''
    },
    onOpen() {
      this.previouslyFocused = this.returnFocus || document.activeElement
      this.campaignShareUrl = cleanCampaignShareUrl()
      this.campaignLinkCopied = false
      this.qrSvg = ''
      if (!this._releasePageScroll) this._releasePageScroll = acquirePageScrollLock()
      document.addEventListener('keydown', this.onKeydown)
      this.generateQr()
      this.$nextTick(() => {
        if (!this.open) return
        this.$refs.closeBtn?.focus?.({ preventScroll: true })
        this.inertBackground()
      })
    },
    onClose() {
      clearTimeout(this._campaignCopyTimer)
      document.removeEventListener('keydown', this.onKeydown)
      this._releasePageScroll?.()
      this._releasePageScroll = null
      this.restoreBackground()
      // Refocus synchronously (not on nextTick): the fundraiser handoff opens
      // V2SupportModal right after this, and it records document.activeElement
      // as its own close-time focus target — that must be the original opener.
      const target = this.previouslyFocused
      this.previouslyFocused = null
      if (target && typeof target.focus === 'function' && target.isConnected !== false) {
        target.focus({ preventScroll: true })
        // v-show has not necessarily restored the sticky opener yet. Retry
        // after that patch, unless a subsequent dialog made it inert again.
        this.$nextTick(() => {
          if (!this.open && target.isConnected !== false && !target.closest('[inert]')) {
            target.focus({ preventScroll: true })
          }
        })
      }
    },
    // Same approach as V2SupportModal: mark every sibling along the hub's
    // ancestor chain inert + aria-hidden, so virtual-cursor screen-reader
    // users can't reach (or open another modal from) the page behind it.
    inertBackground() {
      const root = this.$refs.panel?.parentElement
      if (!root || this._inertedEls) return
      this._inertedEls = []
      let node = root
      while (node && node !== document.body && node.parentElement) {
        for (const sib of node.parentElement.children) {
          if (sib === node || ['SCRIPT', 'STYLE', 'LINK'].includes(sib.tagName)) continue
          this._inertedEls.push({ el: sib, ariaHidden: sib.getAttribute('aria-hidden'), inert: sib.inert })
          sib.setAttribute('aria-hidden', 'true')
          if ('inert' in sib) sib.inert = true
        }
        node = node.parentElement
      }
    },
    restoreBackground() {
      if (!this._inertedEls) return
      this._inertedEls.forEach(({ el, ariaHidden, inert }) => {
        if (ariaHidden === null) el.removeAttribute('aria-hidden')
        else el.setAttribute('aria-hidden', ariaHidden)
        if ('inert' in el) el.inert = inert
      })
      this._inertedEls = null
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.close()
    },
    close() {
      this.$emit('close')
    },
    startFundraiser() {
      this.$emit('start-fundraiser')
    },
    trapFocus(event) {
      const panel = this.$refs.panel
      if (!panel) return
      const controls = [...panel.querySelectorAll('button, input, a[href], [tabindex="0"]')]
        .filter(el => !el.disabled && el.getClientRects().length)
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    },
    async generateQr() {
      const id = this.campaign?.id
      const target = id && typeof window !== 'undefined'
        ? `${window.location.origin}/qr/${id}`
        : this.campaignShareUrl
      if (!target) return
      try {
        // Lazy chunk — the qrcode lib stays out of the main bundle.
        const QRCode = (await import('qrcode')).default
        const svg = await QRCode.toString(target, {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 112,
          color: { dark: '#1D2549', light: '#ffffff' },
        })
        if (this.open) this.qrSvg = svg
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('V2SupportHubModal: QR generation failed', err)
        this.qrSvg = ''
      }
    },
    async copyCampaignLink() {
      const ok = await copyTextToClipboard(this.campaignShareUrl, this.$refs.campaignLinkInput)
      if (!ok) return
      this.campaignLinkCopied = true
      clearTimeout(this._campaignCopyTimer)
      this._campaignCopyTimer = setTimeout(() => { this.campaignLinkCopied = false }, 2000)
    },
    openShare(opt) {
      openShareOption(opt, { onCopy: () => this.copyCampaignLink() })
    },
  },
}
</script>

<style scoped>
/* ─── Backdrop + panel ────────────────────────────────────── */
.v2-hub {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(29, 37, 73, 0.75);
  backdrop-filter: blur(2px);
  font-family: 'Red Hat Text', sans-serif;
  color: var(--sideout-navy, #1D2549);
}
.v2-hub__panel {
  position: relative;
  width: min(1080px, 94vw);
  max-height: min(880px, 92vh);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}
.v2-hub__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 36px 40px 32px;
}
.v2-hub__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 6;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: #DA1884;
  color: #ffffff;
  cursor: pointer;
  transition: background 180ms ease, transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2-hub__close:hover { background: #BC0E65; transform: scale(1.06); }
.v2-hub__close:active { background: #BC0E65; transform: scale(0.98); }
.v2-hub__close:focus-visible { outline: 3px solid rgba(218, 24, 132, 0.4); outline-offset: 2px; }

/* ─── Header ──────────────────────────────────────────────── */
.v2-hub__header { max-width: 760px; padding-right: 40px; }
.v2-hub__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #DA1884;
}
.v2-hub__eyebrow-dot { width: 8px; height: 8px; border-radius: 50%; background: #DA1884; }
.v2-hub__title {
  margin: 8px 0 0;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 32px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.01em;
}
.v2-hub__parent {
  display: inline-block;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #009CDE;
  text-decoration: none;
}
.v2-hub__parent:hover { text-decoration: underline; }
.v2-hub__headline {
  margin: 16px 0 4px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}
.v2-hub__intro { font-size: 15px; line-height: 1.55; color: #4B5563; }
.v2-hub__intro :deep(p) { margin: 0 0 8px; }
.v2-hub__intro :deep(p:last-child) { margin-bottom: 0; }

/* ─── Cards ───────────────────────────────────────────────── */
.v2-hub__cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: stretch;
}
.v2-hub__card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 22px 22px;
  background: #F8F9FC;
  border: 1px solid #E5E7EB;
  border-radius: 18px;
  overflow: hidden;
}
.v2-hub__card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}
.v2-hub__card--fundraise::before { background: #009CDE; }
.v2-hub__card--share::before { background: linear-gradient(90deg, #DA1884, #7C4DFF); }
.v2-hub__card-head { display: flex; gap: 14px; align-items: flex-start; }
.v2-hub__card-icon {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}
.v2-hub__card-icon svg { width: 22px; height: 22px; }
.v2-hub__card-icon--blue { background: rgba(0, 156, 222, 0.12); color: #009CDE; }
.v2-hub__card-icon--pink { background: rgba(218, 24, 132, 0.12); color: #DA1884; }
.v2-hub__card-title {
  margin: 0;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}
.v2-hub__card-sub { margin: 4px 0 0; font-size: 14px; line-height: 1.45; color: #6B7280; }

.v2-hub__benefits {
  flex: 1;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.v2-hub__benefits li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.4;
  color: #4B5563;
}
.v2-hub__benefits strong { display: block; font-weight: 700; color: var(--sideout-navy, #1D2549); }
.v2-hub__check {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #009CDE;
  color: #ffffff;
}

.v2-hub__cta {
  margin-top: auto;
  min-height: 52px;
  padding: 0 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 9999px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  cursor: pointer;
  transition: background-color 150ms ease, transform 150ms ease;
}
.v2-hub__cta--blue { background: #009CDE; }
.v2-hub__cta--blue:hover { background: #0088C2; transform: translateY(-1px); }
.v2-hub__cta:focus-visible { outline: 3px solid rgba(0, 156, 222, 0.4); outline-offset: 2px; }

/* ─── Share card internals ────────────────────────────────── */
.v2-hub__qr-panel {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 14px;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
}
.v2-hub__qr { flex: 0 0 112px; width: 112px; height: 112px; }
.v2-hub__qr :deep(svg) { display: block; width: 100%; height: 100%; }
.v2-hub__qr--placeholder { background: #F3F4F6; border-radius: 8px; }
.v2-hub__qr-copy { min-width: 0; }
.v2-hub__qr-label {
  font-family: 'Red Hat Display', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #DA1884;
}
.v2-hub__qr-copy h4 {
  margin: 2px 0 4px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px;
  font-weight: 800;
}
.v2-hub__qr-copy p { margin: 0; font-size: 13px; line-height: 1.45; color: #6B7280; }

.v2-hub__copy-row { display: flex; gap: 8px; }
.v2-hub__url {
  flex: 1;
  min-width: 0;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: #ffffff;
  font-size: 13px;
  color: #4B5563;
}
.v2-hub__copy-btn {
  flex: 0 0 auto;
  height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  background: #DA1884;
  color: #ffffff;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 150ms ease;
}
.v2-hub__copy-btn:hover { background: #BC0E65; }
.v2-hub__copy-btn.is-copied { background: #22C55E; }

.v2-hub__social-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  gap: 8px;
}
.v2-hub__social {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 6px;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--sideout-navy, #1D2549);
  cursor: pointer;
  transition: border-color 150ms ease, transform 150ms ease;
}
.v2-hub__social:hover { border-color: #DA1884; transform: translateY(-1px); }
.v2-hub__social-icon :deep(svg) { width: 18px; height: 18px; }
.v2-hub__social--facebook .v2-hub__social-icon { color: #1877F2; }
.v2-hub__social--twitter .v2-hub__social-icon { color: #000000; }
.v2-hub__social--linkedin .v2-hub__social-icon { color: #0A66C2; }
.v2-hub__social--whatsapp .v2-hub__social-icon { color: #25D366; }
.v2-hub__social--email .v2-hub__social-icon { color: #DA1884; }
.v2-hub__social--sms .v2-hub__social-icon { color: #22C55E; }
.v2-hub__social--instagram .v2-hub__social-icon { color: #E1306C; }

/* ─── Responsive ──────────────────────────────────────────── */
@media (max-width: 1023px) {
  .v2-hub__cards { grid-template-columns: 1fr; }
}
@media (max-width: 767px) {
  .v2-hub { padding: 0; }
  .v2-hub__panel {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
  }
  .v2-hub__scroll { gap: 18px; padding: 60px 20px 28px; }
  .v2-hub__header { padding-right: 0; }
  .v2-hub__title { font-size: 26px; }
  .v2-hub__headline { font-size: 18px; }
  .v2-hub__card { padding: 20px 18px 18px; }
  .v2-hub__qr-panel { flex-direction: column; text-align: center; }
}

/* ─── Transitions ─────────────────────────────────────────── */
.v2-hub-enter-active, .v2-hub-leave-active { transition: opacity 220ms ease; }
.v2-hub-enter-active .v2-hub__panel,
.v2-hub-leave-active .v2-hub__panel {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease;
}
.v2-hub-enter-from, .v2-hub-leave-to { opacity: 0; }
.v2-hub-enter-from .v2-hub__panel, .v2-hub-leave-to .v2-hub__panel {
  transform: translateY(12px) scale(0.97);
}
@media (prefers-reduced-motion: reduce) {
  .v2-hub-enter-active, .v2-hub-leave-active,
  .v2-hub-enter-active .v2-hub__panel, .v2-hub-leave-active .v2-hub__panel { transition-duration: 60ms; }
  .v2-hub-enter-from .v2-hub__panel, .v2-hub-leave-to .v2-hub__panel { transform: none; }
  .v2-hub__close, .v2-hub__close:hover, .v2-hub__close:active,
  .v2-hub__cta--blue:hover, .v2-hub__social:hover { transform: none; }
}
</style>
